import { useState, type MouseEvent, type ReactNode } from 'react';

import './TruncatedText.scss';

/** Whether an element's content is wider than its box, i.e. its ellipsis is hiding part of it. */
export const isTruncated = (element: HTMLElement): boolean => element.scrollWidth > element.clientWidth;

interface TruncatedTextProps {
  /** The full value: the tooltip, and what is shown unless `children` render it differently. */
  readonly text: string;
  /** The same value rendered with markup, e.g. a search match highlighted. */
  readonly children?: ReactNode;
}

/**
 * One line of text, cut with an ellipsis when it does not fit, whose full value shows as a native tooltip — only while it
 * is actually cut, so a value that fits gets no tooltip repeating it. The roadmap's `Text` with a `truncate` prop
 * (`component-architecture.md`), ahead of the primitives it would belong to.
 *
 * It owns the ellipsis and nothing else: font and colour are inherited, and it fills the width its parent gives it, so a
 * flex parent must let it shrink (`min-width: 0` along the row). The cut is measured on hover rather than observed,
 * because hovering is the only moment the tooltip matters.
 *
 * A native `title` rather than a react-bootstrap `Tooltip`: it costs nothing per row in a long list, and it works inside
 * third-party render-props. Like any tooltip it needs pointer events, so under a `pointer-events: none` ancestor the call
 * site must re-enable them on this text.
 */
export const TruncatedText = ({ text, children }: TruncatedTextProps) => {
  const [cut, setCut] = useState(false);
  const measure = (event: MouseEvent<HTMLSpanElement>) => setCut(isTruncated(event.currentTarget));

  return (
    <span className="truncated-text" title={cut ? text : undefined} onMouseEnter={measure}>
      {children ?? text}
    </span>
  );
};
